import React, { useState, useEffect } from 'react';
import { 
  Package, Truck, Clock, MapPin, Phone, Search, RefreshCw, 
  User, CheckCircle, Navigation, CreditCard, Banknote,
  Calendar, Eye, Edit, Download, Filter, SortAsc, SortDesc,
  Loader2, AlertCircle, Activity, Zap, Timer, PlayCircle
} from 'lucide-react';

const ExpedieriActive = ({ 
  shipments = [], 
  loading = false, 
  error = null, 
  onRefresh,
  onUpdateShipment,
  drivers = [],
  vehicles = [] 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('prioritate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedStatus, setSelectedStatus] = useState('toate');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Auto-refresh la fiecare 30 de secunde
  useEffect(() => {
    let interval;
    if (autoRefresh && onRefresh) {
      interval = setInterval(() => {
        onRefresh();
        setLastUpdate(new Date());
      }, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, onRefresh]);

  // Filtrează doar expedierile active (nu livrate)
  const activeShipments = shipments.filter(shipment => 
    shipment.status !== 'livrat' && shipment.status !== 'anulat'
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pe_drum': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_asteptare': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_incarcare': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pe_drum': return 'Pe drum';
      case 'in_asteptare': return 'În așteptare';
      case 'in_incarcare': return 'În încărcare';
      default: return status || 'Necunoscut';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pe_drum': return <Navigation className="h-4 w-4" />;
      case 'in_asteptare': return <Clock className="h-4 w-4" />;
      case 'in_incarcare': return <Package className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (prioritate) => {
    return prioritate === 'urgenta' ? 'border-l-4 border-red-500' : 'border-l-4 border-blue-200';
  };

  const filteredShipments = activeShipments.filter(shipment => {
    const matchesStatus = selectedStatus === 'toate' || shipment.status === selectedStatus;
    const matchesSearch = 
      (shipment.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (shipment.client_nume || shipment.client || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (shipment.sofer || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (shipment.ruta || shipment.destinatie || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const sortedShipments = [...filteredShipments].sort((a, b) => {
    // Prioritatea urgentă întotdeauna prima
    if (a.prioritate === 'urgenta' && b.prioritate !== 'urgenta') return -1;
    if (b.prioritate === 'urgenta' && a.prioritate !== 'urgenta') return 1;
    
    let aValue = a[sortBy] || '';
    let bValue = b[sortBy] || '';
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const statusCounts = {
    toate: activeShipments.length,
    pe_drum: activeShipments.filter(s => s.status === 'pe_drum').length,
    in_asteptare: activeShipments.filter(s => s.status === 'in_asteptare').length,
    in_incarcare: activeShipments.filter(s => s.status === 'in_incarcare').length
  };

  const urgentCount = activeShipments.filter(s => s.prioritate === 'urgenta').length;

  const handleManualRefresh = () => {
    if (onRefresh) {
      onRefresh();
      setLastUpdate(new Date());
    }
  };

  if (loading) {
    return (
      <main className="flex-1 p-6 overflow-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-lg text-gray-700 font-medium">Se încarcă expedierile active...</p>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex-1 p-6 overflow-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Eroare la încărcarea datelor</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleManualRefresh}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Încearcă din nou
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 overflow-auto">
      {/* Header cu statistici */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="h-6 w-6 text-blue-600" />
              Expedieri Active
            </h1>
            <p className="text-gray-600 mt-1">
              Monitorizare în timp real • Ultima actualizare: {lastUpdate.toLocaleTimeString('ro-RO')}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Auto-refresh (30s)
            </label>
            <button
              onClick={handleManualRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Actualizează
            </button>
          </div>
        </div>

        {/* Statistici rapide */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Active</p>
                <p className="text-2xl font-bold text-gray-900">{statusCounts.toate}</p>
              </div>
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pe Drum</p>
                <p className="text-2xl font-bold text-blue-900">{statusCounts.pe_drum}</p>
              </div>
              <Navigation className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">În Așteptare</p>
                <p className="text-2xl font-bold text-yellow-900">{statusCounts.in_asteptare}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Urgente</p>
                <p className="text-2xl font-bold text-red-900">{urgentCount}</p>
              </div>
              <Zap className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Control Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Caută după ID, client, șofer sau destinație..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="prioritate">Sortează după Prioritate</option>
            <option value="id">Sortează după ID</option>
            <option value="client">Sortează după Client</option>
            <option value="status">Sortează după Status</option>
            <option value="sofer">Sortează după Șofer</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
          </button>
        </div>

        {/* Status Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'toate', label: 'Toate Active', count: statusCounts.toate, color: 'bg-gray-100 text-gray-700' },
            { key: 'pe_drum', label: 'Pe Drum', count: statusCounts.pe_drum, color: 'bg-blue-100 text-blue-700' },
            { key: 'in_asteptare', label: 'În Așteptare', count: statusCounts.in_asteptare, color: 'bg-yellow-100 text-yellow-700' },
            { key: 'in_incarcare', label: 'În Încărcare', count: statusCounts.in_incarcare, color: 'bg-orange-100 text-orange-700' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setSelectedStatus(filter.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                selectedStatus === filter.key
                  ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                  : `${filter.color} border-transparent hover:shadow-md`
              }`}
            >
              {filter.label} ({filter.count})
            </button>
          ))}
        </div>
      </div>

      {/* Expedieri Active Cards */}
      <div className="space-y-4">
        {sortedShipments.map(shipment => (
          <div key={shipment.id} className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow ${getPriorityColor(shipment.prioritate)}`}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* ID și Status */}
              <div className="lg:col-span-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-bold text-gray-900">#{shipment.id}</span>
                  {shipment.prioritate === 'urgenta' && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold animate-pulse">
                      <Zap className="h-3 w-3 inline mr-1" />
                      URGENT
                    </span>
                  )}
                </div>
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border ${getStatusColor(shipment.status)}`}>
                  {getStatusIcon(shipment.status)}
                  {getStatusText(shipment.status)}
                </div>
              </div>

              {/* Client și Destinație */}
              <div className="lg:col-span-3">
                <div className="space-y-2">
                  <div className="flex items-center text-sm font-medium text-gray-900">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    {shipment.client_nume || shipment.client || 'Client necunoscut'}
                  </div>
                  <div className="flex items-start text-sm text-gray-600">
                    <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{shipment.ruta || shipment.destinatie || 'Destinație necunoscută'}</span>
                  </div>
                  {shipment.descriere && (
                    <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                      {shipment.descriere}
                    </div>
                  )}
                </div>
              </div>

              {/* Șofer și Transport */}
              <div className="lg:col-span-3">
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="font-medium text-gray-900">{shipment.sofer || 'Șofer nealocat'}</span>
                  </div>
                  {shipment.sofer_telefon && (
                    <div className="flex items-center text-sm">
                      <Phone className="h-3 w-3 mr-2 text-gray-400" />
                      <a 
                        href={`tel:${shipment.sofer_telefon}`} 
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {shipment.sofer_telefon}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <Truck className="h-3 w-3 mr-2 text-gray-400" />
                    {shipment.vehicul_complet || shipment.vehicul_numar || 'Vehicul nealocat'}
                  </div>
                </div>
              </div>

              {/* Plată și Programare */}
              <div className="lg:col-span-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {(shipment.tip_plata || 'cash') === 'card' ? (
                      <CreditCard className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Banknote className="h-4 w-4 text-green-600" />
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      (shipment.tip_plata || 'cash') === 'card' 
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {(shipment.tip_plata || 'cash') === 'card' ? 'Card' : 'Cash'}
                    </span>
                  </div>
                  
                  {(shipment.tip_plata || 'cash') === 'cash' && shipment.valoare_formatata && (
                    <div className="text-sm font-bold text-gray-900">
                      {shipment.valoare_formatata}
                    </div>
                  )}

                  {shipment.data_transport && (
                    <div className="text-xs text-gray-500">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      {shipment.data_transport}
                    </div>
                  )}
                </div>
              </div>

              {/* Acțiuni */}
              <div className="lg:col-span-2">
                <div className="flex gap-2 justify-end">
                  <button 
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Vizualizează detalii"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button 
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Editează expediere"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  {shipment.sofer_telefon && (
                    <button 
                      onClick={() => window.open(`tel:${shipment.sofer_telefon}`)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Sună șofer"
                    >
                      <Phone className="h-4 w-4" />
                    </button>
                  )}
                  {shipment.status !== 'pe_drum' && (
                    <button 
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Pornește expedierea"
                    >
                      <PlayCircle className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedShipments.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <Activity className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {activeShipments.length === 0 ? 'Nu există expedieri active' : 'Nu au fost găsite expedieri'}
          </h3>
          <p className="text-gray-600 mb-4">
            {activeShipments.length === 0 
              ? 'Toate expedierile au fost finalizate sau nu există expedieri în sistem.' 
              : 'Încearcă să modifici criteriile de căutare sau filtrare.'
            }
          </p>
          {activeShipments.length > 0 && (
            <button 
              onClick={() => {
                setSelectedStatus('toate');
                setSearchTerm('');
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Resetează filtrele
            </button>
          )}
        </div>
      )}
    </main>
  );
};

export default ExpedieriActive;